import { apiClient, apiOrigin, type ApiResult, type RequestOptions } from '@/services/apiClient';
import type {
  AiConfigurationInput,
  AiConfigurationResponse,
  AiPreferencesInput,
  AiPreferencesResponse,
  AiKnowledgeInput,
  AiKnowledgeResponse,
  AiGuardrailPolicyInput,
  AiGuardrailRuleInput,
  AiGuardrailRuleUpdate,
  AiGuardrailsResponse,
  AiDeleteInput,
  AiConnectionTestInput,
  AiConnectionTestResult,
} from './aiSettings.types';

const base = `${apiOrigin}/api/v1/ai`;
export class AiSettingsApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string | null,
  ) {
    super(message);
    this.name = 'AiSettingsApiError';
  }
}
async function unwrap<T>(promise: Promise<ApiResult<T>>): Promise<T> {
  const result = await promise;
  if (!result.ok || result.data === undefined || result.data === null)
    throw new AiSettingsApiError(
      result.error ?? 'AI settings could not be loaded or saved.',
      result.status,
      result.errorCode ?? null,
    );
  return result.data;
}
export const aiSettingsApi = {
  getConfiguration: (options?: RequestOptions) =>
    unwrap(apiClient.get<AiConfigurationResponse>(`${base}/configuration`, options)),
  saveConfiguration: (input: AiConfigurationInput, options?: RequestOptions) =>
    unwrap(apiClient.patch<AiConfigurationResponse>(`${base}/configuration`, input, options)),
  getPreferences: (options?: RequestOptions) =>
    unwrap(apiClient.get<AiPreferencesResponse>(`${base}/preferences`, options)),
  savePreferences: (input: AiPreferencesInput, options?: RequestOptions) =>
    unwrap(apiClient.patch<AiPreferencesResponse>(`${base}/preferences`, input, options)),
  getKnowledge: (options?: RequestOptions) =>
    unwrap(apiClient.get<AiKnowledgeResponse>(`${base}/knowledge`, options)),
  createKnowledge: (input: AiKnowledgeInput, options?: RequestOptions) =>
    unwrap(apiClient.post<AiKnowledgeResponse>(`${base}/knowledge`, input, options)),
  deleteKnowledge: ({ id, expectedVersion }: AiDeleteInput, options?: RequestOptions) =>
    unwrap(
      apiClient.delete<AiKnowledgeResponse>(
        `${base}/knowledge/${encodeURIComponent(id)}?expectedVersion=${expectedVersion}`,
        options,
      ),
    ),
  getGuardrails: (options?: RequestOptions) =>
    unwrap(apiClient.get<AiGuardrailsResponse>(`${base}/guardrails`, options)),
  saveGuardrails: (input: AiGuardrailPolicyInput, options?: RequestOptions) =>
    unwrap(apiClient.patch<AiGuardrailsResponse>(`${base}/guardrails`, input, options)),
  createRule: (input: AiGuardrailRuleInput, options?: RequestOptions) =>
    unwrap(apiClient.post<AiGuardrailsResponse>(`${base}/guardrails/rules`, input, options)),
  updateRule: ({ id, ...input }: AiGuardrailRuleUpdate, options?: RequestOptions) =>
    unwrap(
      apiClient.patch<AiGuardrailsResponse>(
        `${base}/guardrails/rules/${encodeURIComponent(id)}`,
        input,
        options,
      ),
    ),
  deleteRule: ({ id, expectedVersion }: AiDeleteInput, options?: RequestOptions) =>
    unwrap(
      apiClient.delete<AiGuardrailsResponse>(
        `${base}/guardrails/rules/${encodeURIComponent(id)}?expectedVersion=${expectedVersion}`,
        options,
      ),
    ),
  testConnection: (
    { provider, expectedVersion }: AiConnectionTestInput,
    options?: RequestOptions,
  ) =>
    unwrap(
      apiClient.post<AiConnectionTestResult>(
        `${base}/providers/${provider}/test`,
        { expectedVersion },
        options,
      ),
    ),
};
